/* eslint-disable jsx-a11y/alt-text */
'use client'

import {
  Anchor,
  Image,
  OpeningTitle,
  Paragraph,
  Section,
  TestimonialCard,
  Title,
} from '@dynamo-caffeina-dev/airc-ui-kit'
import NextImage from 'next/image'
import Link from 'next/link'
import LinkArrowRight from '@/components/atoms/Icons/link-arrow'
import {
  ImageTextModelType,
  LayoutDirectionType,
  LayoutDistributionType,
  NormalizedSection,
  SectionBackground,
} from '@/types'
import { CSSProperties, useMemo } from 'react'
import { useParams } from 'next/navigation'
import { handleOnClickModel } from '@/helpers/models'
import DOMPurify from 'isomorphic-dompurify'
import { dispatchGTMEvent } from '@/lib/tracking/gtm'
import { GTM_EVENT_CATEGORY } from '@/lib/tracking/types/GTM_EVENT_CATEGORY'

export interface ImageTextProps extends NormalizedSection {
  modelledContent: ImageTextModelType
  props?: {
    bg?: string | 'white' | 'dark' | 'black' | 'grey-050'
    containerBg?: string
    direction: LayoutDirectionType
    distribution: LayoutDistributionType
    // NOTE
    // This prop, when set to `true` from the CMS,
    // will remove the section from the graphQL response,
    // so it'll never make it to the frontend to be read
    // by a component. So it's ultimately useless to handle it
    // and should be ignored in frontend code.
    hideSection: boolean
  }
}

const PARTNER_SLUGS = ['partner', 'partners']
const EVENTS_SLUGS = ['calendario-appuntamenti']

function ImageText({ modelledContent, props }: ImageTextProps) {
  const params = useParams()
  // In case of multiple path segments, we just check the 1st one
  // Also, the site can only go 1 level deep
  const slug = Array.isArray(params.slug) ? params.slug[0] : params.slug

  const { openingTitle, title, paragraph, cta, image, testimonialCard } =
    modelledContent
  const { fit, imageProps } = image
  const { bg, containerBg, direction, distribution } = props || {
    bg: 'white',
  }

  const openingTitleLabel = openingTitle?.content
  const titleLabel = title?.content
  const paragraphContent = paragraph?.content
  const ctaLabel = cta?.label

  const isPartnerPage = useMemo(() => PARTNER_SLUGS.includes(slug), [slug])
  const isEventsPage = useMemo(() => EVENTS_SLUGS.includes(slug), [slug])

  // In a "partner" page every ImageText component represents a partner,
  // so the `titleLabel` is the partner's name, not the page title.
  const _title = useMemo(
    () =>
      isPartnerPage || isEventsPage
        ? DOMPurify.sanitize(titleLabel ?? '', {
            USE_PROFILES: { html: false },
          })
        : '',

    [titleLabel, isPartnerPage, isEventsPage]
  )

  return (
    <Section
      bg={bg as SectionBackground | undefined}
      className={`section-image-text`}
      data-container-bg={!!containerBg}
      data-direction={direction ?? 'row'}
      data-distribution={distribution ?? 'text-image'}
      style={
        {
          '--container-bg': `var(--airc-ui__colors-${containerBg})`,
        } as CSSProperties
      }
    >
      <div className='section-image-text__grid'>
        <div className='section-image-text__grid__col-1'>
          <div className={`section-image-text__title-wrapper`}>
            {openingTitleLabel ? (
              <OpeningTitle
                className={`section-image-text__opening-title`}
                {...openingTitle}
              >
                {openingTitleLabel}
              </OpeningTitle>
            ) : null}

            {titleLabel ? (
              <Title
                className={`section-image-text__title`}
                {...title}
                dangerouslySetInnerHTML={{ __html: titleLabel }}
              />
            ) : null}
          </div>

          {paragraphContent ? (
            <Paragraph
              // We override Paragraph's default alignment by following the Title's
              // alignment; this is needed because the WYSIWYG editor won't pass
              // any style for its default alignment (which is left).
              // If the user sets an alignment other than left in the WYSIWYG editor,
              // the resulting HTML with inline styles will override this alignment.
              alignment={title?.alignment}
              as={'div'}
              className={`section-image-text__paragraph`}
              dangerouslySetInnerHTML={{ __html: paragraphContent }}
              {...paragraph}
            />
          ) : null}

          {testimonialCard ? (
            // @ts-ignore
            <TestimonialCard
              className={'section-image-text__testimonial-card'}
              {...testimonialCard}
              {...(testimonialCard.onClick
                ? { onClick: handleOnClickModel(testimonialCard.onClick) }
                : {})}
            />
          ) : null}

          {cta && ctaLabel ? (
            <Anchor
              wrapper={'div'}
              data-alignment={cta?.alignment}
              className={`section-image-text__cta link-button-themed`}
              // variant={'themed'}
              as={Link}
              {...cta}
              linkProps={{
                onClickCapture: () => {
                  // (assumes ALL components in the partner page
                  // are used to display partnerships).
                  if (isPartnerPage) {
                    dispatchGTMEvent(GTM_EVENT_CATEGORY.PARTNER_CLICKTHROUGH, {
                      cta_click: _title,
                    })
                  } else if (isEventsPage) {
                    dispatchGTMEvent(GTM_EVENT_CATEGORY.EVENT_CLICKTHROUGH, {
                      section: _title,
                    })
                  } else {
                    dispatchGTMEvent(GTM_EVENT_CATEGORY.GENERIC_CTA_CLICK, {
                      cta_click: ctaLabel,
                    })
                  }
                },
              }}
            >
              {ctaLabel}
              <LinkArrowRight className={`section-image-text__cta__icon`} />
            </Anchor>
          ) : null}
        </div>

        <div className='section-image-text__grid__col-2'>
          {imageProps?.src ? (
            <Image
              fit={fit}
              className={`section-image-text__image`}
              as={NextImage}
              imageProps={{
                ...imageProps,
                // If not present (which it will not be,
                // since the CMS is not returning them),
                // it'll take the default value.
                // TODO find out why these props were expected here,
                // @ts-ignore
                width: imageProps.width || 400,
                // @ts-ignore
                height: imageProps.height || 400,
              }}
            />
          ) : null}

          {testimonialCard ? (
            // @ts-ignore
            <TestimonialCard
              className={'section-image-text__testimonial-card'}
              {...testimonialCard}
              {...(testimonialCard.onClick
                ? { onClick: handleOnClickModel(testimonialCard.onClick) }
                : {})}
            />
          ) : null}
        </div>
      </div>
    </Section>
  )
}

export default ImageText
